import { InjectionToken } from '@angular/core';

/**
 * How the session list turns Software Security user ids into people, and a
 * typed user query into an id (#30072).
 *
 * An OPTIONAL host-provided hook, for the same reason the token factory is one:
 * this lib cannot know where user records live. web-software-security holds the
 * Software Security user table and can answer both questions; another host may
 * only answer one, or none. A host that provides nothing gets the honest
 * degraded behaviour — Guid fragments in the user column, and a user filter
 * that accepts ids but refuses emails with a message.
 *
 * Both methods answer `undefined`/missing entries for "I do not know", never
 * throw for it. Unknown ids are a NORMAL state (the user left the company, or
 * the id was asserted by a client this resolver has never heard of), and the
 * caller keeps the Guid fragment for exactly that case.
 */
export interface IRynReplayUserDisplay {
  /** Full name, or email when no name is on file. */
  name: string;
  /** Email when it adds information beyond `name`. */
  email?: string;
}

/** One row in the user filter autocomplete (#31323). */
export interface IRynReplayUserSuggestion {
  userId: string;
  /** Text shown in the dropdown. */
  label: string;
  /**
   * Value written back into the filter box on pick. Prefer email when present
   * so Apply resolves uniquely without another ambiguity probe.
   */
  queryValue: string;
}

export interface IRynReplayUserResolver {
  /**
   * Display names for a batch of Software Security user ids. Ids missing from
   * the returned map render as Guid fragments. Batched so a 50-row page is one
   * lookup, not fifty.
   */
  displayNames(
    securityUserIds: readonly string[]
  ): Promise<ReadonlyMap<string, IRynReplayUserDisplay>>;

  /**
   * Resolve a human-typed user query (email, or a name unique enough to match
   * exactly one person) to a Software Security user id. `undefined` means "no
   * such user", which the caller treats as a refusal to search rather than a
   * dropped filter.
   */
  findUserId(emailOrName: string): Promise<string | undefined>;

  /**
   * Typeahead suggestions for the user filter box (#31323). Returns up to
   * `limit` matches; ambiguous prefixes list every candidate rather than
   * picking one. Hosts without a user table may omit this — the box stays a
   * plain search field.
   */
  suggestUsers?(
    query: string,
    limit?: number
  ): Promise<readonly IRynReplayUserSuggestion[]>;
}

export const RYN_REPLAY_USER_RESOLVER =
  new InjectionToken<IRynReplayUserResolver>('RYN_REPLAY_USER_RESOLVER');

/**
 * Match a typed user query against names already resolved for the current
 * page. Exact name or email only — partial matching is left to findUserId so
 * a substring like "dustin" does not collide with "dustina" on the page cache.
 */
export const matchResolvedReplayUserId = (
  query: string,
  names: ReadonlyMap<string, IRynReplayUserDisplay>
): string | undefined => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return undefined;
  }
  const hits: string[] = [];
  for (const [id, display] of names) {
    const name = display.name.trim().toLowerCase();
    const email = display.email?.trim().toLowerCase();
    if (name === needle || email === needle) {
      hits.push(id);
    }
  }
  return hits.length === 1 ? hits[0] : undefined;
};
